<!-- Transient status, undo, and the screen-reader live regions. Factory-owned.

     Mounted once by App.vue. Everything here is driven from a module, so any
     part of the app can raise a toast or offer an undo without being able to
     see this component — `toast()` from core/util.js, `removeWithUndo()` from
     core/undo.js, `announce()` from core/a11y.js.

     UNDO IS OFFERED INSTEAD OF "are you sure?", NOT AS WELL AS IT.
     A confirm dialog interrupts every delete to prevent the rare wrong one;
     undo interrupts none of them and fixes the wrong one afterwards. The
     exception is anything that genuinely cannot be undone — that still needs
     the dialog, and should say why.

     `ion-toast` rather than a hand-rolled bar: it already places itself above
     the tab bar and inside the safe area, it announces itself, and it renders
     as iOS on iOS and Material on Android. The version of this that was two
     fixed-position divs got the bottom inset wrong on exactly one device. -->
<template>
  <!-- Live regions. These used to live in AppShell, which Ionic replaced —
       losing them makes `announce()` a silent no-op, and nothing errors. -->
  <div class="srOnly" role="status" aria-live="polite">{{ politeMessage }}</div>
  <div class="srOnly" role="alert" aria-live="assertive">{{ assertiveMessage }}</div>

  <!-- Long enough to notice and act on: the window is the only thing standing
       between the user and a permanent delete. Dismissing it commits, which is
       the same thing the timer does. -->
  <!-- `:key` is load-bearing, not tidiness.
       Deleting two things quickly is ordinary, and `remove()` commits the
       first before opening the second — so `pending` goes object → null →
       object inside one tick. Vue batches that into no change at all, Ionic
       never sees `is-open` flip, and the second undo toast simply does not
       appear. Which is the worst possible failure for this component: the
       delete still happened, and the only way to take it back is gone. Keying
       on the pending item tears the toast down and builds a new one. -->
  <IonToast
    :key="round"
    :is-open="!!pending"
    :message="pending?.text"
    :duration="0"
    position="bottom"
    :buttons="undoButtons"
    @didDismiss="flush"
  />

  <!-- `:duration="0"` on purpose: core/util.js owns the timing.
       `toast()` already runs its own timer and calls `dismiss(id)`, which
       removes the entry and takes this element with it. Giving Ionic a
       duration as well means two clocks, and the shorter one wins — which
       silently broke the update prompt, raised with `ms: 0` to mean "stay
       until something dismisses you" and then hidden by Ionic 2.2s later. -->
  <IonToast
    v-for="t in toasts"
    :key="t.id"
    :is-open="true"
    :message="t.text"
    :duration="0"
    position="bottom"
    :color="t.kind === 'bad' ? 'danger' : t.kind === 'update' ? 'primary' : undefined"
    :buttons="t.onClick ? [{ text: label(t), handler: t.onClick }] : undefined"
    @didDismiss="dismiss(t.id)"
  />
</template>

<script setup>
import { ref, watch } from 'vue';
import { IonToast } from '@ionic/vue';
import { toasts, dismiss } from '../util.js';
import { pending, undo, flush } from '../undo.js';
import { politeMessage, assertiveMessage } from '../a11y.js';

// `role: 'cancel'` so the handler runs and the toast closes without ALSO
// firing didDismiss's commit — Ionic dismisses with the button's role, and
// flush() on a reverted delete would put the item back and then delete it.
const undoButtons = [{ text: 'Undo', role: 'cancel', handler: undo }];

// Bumped every time a NEW undo is offered — see the note on :key above.
const round = ref(0);
watch(pending, (p) => { if (p) round.value += 1; });

// The whole toast used to be the tap target. An Ionic toast puts the action on
// a button, so the button has to say what it does — "Open" on an update prompt
// is the sort of label people ignore twice and then report as broken.
const label = (t) => (t.kind === 'update' ? 'Refresh' : 'Open');
</script>

<style scoped>
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
